/**
 * Settings → Notifications → the diagnostics panel — V7-01 §1.
 *
 * The permanent half of the V7-01 fix. The gesture bug is fixed once; this is
 * what makes the *next* one findable, because the founder's report — "keys
 * generated, nothing arrives, site not even in the permission list" — was four
 * questions that no screen in the app could answer.
 *
 * Five rows, each with a state and a remedy in plain words, and a real send at
 * the end. The remedies are written out per browser (and, on a Mac, per OS
 * setting) because "check your browser settings" is the sentence that ends a
 * support conversation without ending the problem.
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, Loader2, RefreshCw, Send, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { describeError } from '@/lib/errorMessage';
import {
  evaluateChecks,
  readEnvironment,
  type DiagnosticCheck,
} from './pushChecks';
import {
  usePushDeliveryHops,
  usePushDevices,
  usePushPublicKey,
  useSendTestPush,
  type DeliveryHops,
} from './pushApi';

function StatusIcon({ status }: { status: DiagnosticCheck['status'] }) {
  if (status === 'pass') return <Check className="mt-0.5 size-3.5 shrink-0 text-[var(--accent)]" />;
  if (status === 'fail') return <X className="mt-0.5 size-3.5 shrink-0 text-[var(--danger)]" />;
  return <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-[var(--warn)]" />;
}

export function PushDiagnostics() {
  const { data: devices = [] } = usePushDevices();
  const { data: key } = usePushPublicKey();
  const test = useSendTestPush();

  const [checks, setChecks] = useState<DiagnosticCheck[] | null>(null);
  const [testRow, setTestRow] = useState<DiagnosticCheck | null>(null);
  /** V8-01 §1.5 — what the service worker did with the last push it received. */
  const [workerRow, setWorkerRow] = useState<DiagnosticCheck | null>(null);
  /** V9-03 §2 — the delivery rows the last test send is being traced through. */
  const [hopIds, setHopIds] = useState<string[]>([]);
  const [hops, setHops] = useState<DeliveryHops | null>(null);
  const readHops = usePushDeliveryHops();

  /**
   * V9-03 §2 — the loopback, watched until it closes.
   *
   * Hop four arrives from a different process on its own schedule, so this
   * polls rather than waiting on the send's response. It stops as soon as the
   * notification is reported displayed, and after fifteen seconds regardless —
   * at which point "not displayed" is itself the answer, and the honest thing
   * is to show it red rather than spin.
   */
  useEffect(() => {
    if (hopIds.length === 0) return;

    let cancelled = false;
    const started = Date.now();

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      try {
        const next = await readHops.mutateAsync(hopIds);
        if (cancelled) return;
        setHops(next);
        if (next.displayed || Date.now() - started > 15_000) return;
      } catch {
        if (Date.now() - started > 15_000) return;
      }
      window.setTimeout(() => void tick(), 1_000);
    };

    void tick();
    return () => {
      cancelled = true;
    };
    // Keyed on `hopIds` alone: `readHops` is a new object every render, and
    // depending on it would restart the poll on every tick.
  }, [hopIds]);

  /**
   * The last link in the chain, reporting for itself — V8-01 §1.5.
   *
   * Everything else on this panel is measured from the page. The service worker
   * is a separate process that the page cannot inspect, so a push that arrived
   * and then failed to render looked identical to one that never arrived — and
   * "nothing arrives" was the entire founder report. The worker now posts what
   * it did, and this row says so.
   */
  useEffect(() => {
    const onMessage = (event: MessageEvent): void => {
      const data = event.data as
        | { type?: string; ok?: boolean; shown?: boolean; degraded?: string | null; error?: string }
        | undefined;
      if (data?.type !== 'vt:push-diagnostic') return;

      setWorkerRow({
        id: 'worker-display',
        label: 'Service worker',
        status: data.ok ? (data.degraded ? 'warn' : 'pass') : 'fail',
        detail: data.ok
          ? `A push arrived and a notification was shown${
              data.degraded ? ` (payload: ${data.degraded})` : ''
            }.`
          : 'A push arrived and the notification could not be shown.',
        remedy: data.ok
          ? undefined
          : `${data.error ?? 'The browser refused to show it.'} — check that notifications ` +
            'are allowed for this site at the operating-system level.',
      });
    };

    navigator.serviceWorker?.addEventListener('message', onMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', onMessage);
  }, []);

  const refresh = useCallback(async () => {
    const env = await readEnvironment({
      registeredDevices: devices.length,
      platformConfigured: Boolean(key?.configured),
    });
    setChecks(evaluateChecks(env));
  }, [devices.length, key?.configured]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function sendTest(): Promise<void> {
    setTestRow(null);
    try {
      const result = await test.mutateAsync();
      const failures = result.details.filter((d) => !d.ok);
      const viaConsole = result.transport === 'console';

      /**
       * V9-03 §0 — the panel names the key source instead of guessing.
       *
       * It used to say "Generate a VAPID key pair in /platform" whenever the
       * transport was console. The founder read that beside a freshly
       * regenerated pair and spent an evening on the wrong problem. The four
       * states need four different sentences, and only the server knows which
       * one is true.
       */
      const SOURCE_TEXT: Record<string, string> = {
        platform: 'platform keys',
        env: 'environment keys',
        none: 'no keys stored',
        unreadable: 'keys stored but unreadable',
      };
      const SOURCE_REMEDY: Record<string, string> = {
        none: 'Generate a VAPID key pair in /platform → Browser push.',
        unreadable:
          'The stored VAPID key will not decrypt — the SECRET_KEY that encrypted it is not ' +
          'the one running now. Re-generate the pair in /platform → Browser push, and expect ' +
          'other secrets encrypted under the old key to need the same.',
        env: 'These come from environment variables. Generate a pair in /platform → Browser ' +
          'push so key rotation does not need a deploy.',
        platform: '',
      };

      /**
       * V8-01 §2 — a console send is never a pass.
       *
       * It used to report `pass` with an explanatory suffix, and that is
       * precisely the masquerade this document forbids: somebody presses the
       * button, sees green, and concludes push works. It proves the server can
       * write a line to its own outbox and nothing whatever about whether a
       * browser would receive anything. `warn` says "this ran, and it did not
       * test the thing you wanted tested".
       */
      setTestRow({
        id: 'test-send',
        label: 'Delivery',
        /**
         * §0 — "a console-transport delivery on an installation with stored
         * keys is a warning state, always." Console is never a pass.
         */
        status: result.sent === 0 ? 'fail' : viaConsole ? 'warn' : 'pass',
        detail:
          result.devices === 0
            ? 'No devices registered, so there was nothing to send to.'
            : `${result.sent} of ${result.devices} device(s) accepted the push, over the ` +
              `${viaConsole ? 'console' : 'real web-push'} transport, using ` +
              `${SOURCE_TEXT[result.keySource] ?? result.keySource}` +
              `${result.pin ? ` — ${result.pin}` : ''}.`,
        // Verbatim: "VAPID credentials mismatch" and "endpoint gone" need
        // opposite fixes.
        remedy:
          failures.length > 0
            ? failures.map((f) => `${f.label}: ${f.error}`).join(' · ')
            : viaConsole
              ? [
                  'Nothing left the server on the console transport.',
                  result.pin
                    ? 'The transport is pinned by configuration — clear PUSH_TRANSPORT and restart.'
                    : '',
                  SOURCE_REMEDY[result.keySource] ?? '',
                ]
                  .filter(Boolean)
                  .join(' ')
              : undefined,
      });

      /* §2 — the four hops, polled until the worker reports or gives up. */
      if (result.deliveryIds.length > 0) setHopIds(result.deliveryIds);
      // A successful send may have pruned a dead endpoint, so re-read.
      await refresh();
    } catch (err) {
      setTestRow({
        id: 'test-send',
        label: 'Delivery',
        status: 'fail',
        detail: 'The test could not be sent.',
        remedy: describeError(err, 'The server refused the request.'),
      });
    }
  }

  /**
   * §2 — "the panel then shows all four hops with ✓/✗".
   *
   * One row per hop, because the whole point is that a person can say *which*
   * one is red. A single "delivery failed" is what they have been reporting for
   * three versions.
   */
  const hopRows: DiagnosticCheck[] = hops
    ? [
        {
          id: 'hop-recorded',
          label: '1 · Server recorded it',
          status: hops.recorded ? 'pass' : 'fail',
          detail: hops.recorded
            ? 'A delivery row exists for this send.'
            : 'No delivery row was written — the send never reached the push service code.',
        },
        {
          id: 'hop-accepted',
          label: '2 · Push service accepted it',
          status: hops.accepted ? 'pass' : 'fail',
          detail: hops.accepted
            ? 'The push service took the message.'
            : 'The push service did not accept it — see the Delivery row above for what it said.',
        },
        {
          id: 'hop-displayed',
          label: '3 · Your browser showed it',
          status: hops.displayed ? 'pass' : 'fail',
          detail: hops.displayed
            ? 'The service worker reported the notification was displayed.'
            : 'The service worker has not reported showing it. If nothing appeared, the ' +
              'notification was blocked at the operating-system level or the worker is stale.',
          remedy: hops.displayed
            ? undefined
            : 'On macOS check System Settings → Notifications → your browser, and confirm ' +
              'Focus is off. Then reload this page to pick up the newest service worker.',
        },
      ]
    : [];

  const rows = [
    ...(checks ?? []).map((check) => (check.id === 'test-send' && testRow ? testRow : check)),
    // Appended rather than substituted: it is a *different* claim from the
    // send — the server let go of it, and then the browser did or did not
    // render it — and collapsing the two is how the display half stayed
    // invisible for two versions.
    ...(workerRow ? [workerRow] : []),
    ...hopRows,
  ];

  return (
    <section className="vt-surface flex flex-col gap-2 p-3" data-testid="push-diagnostics">
      <div className="flex items-center gap-2">
        <span className="flex-1 text-[13px] font-medium">Push diagnostics</span>
        <Button variant="ghost" size="sm" onClick={() => void refresh()} data-testid="push-diagnostics-refresh">
          <RefreshCw className="size-3.5" />
          Re-check
        </Button>
      </div>
      <p className="text-[12px] vt-muted">
        Five checks, in the order the browser performs them. If push is not working, the first ✗ is
        the reason.
      </p>

      <ul className="flex flex-col gap-1" data-testid="push-diagnostics-list">
        {rows.map((check) => (
          <li
            key={check.id}
            data-testid={`push-check-${check.id}`}
            data-status={check.status}
            className="flex items-start gap-2 rounded-[6px] border border-[var(--border)] px-2 py-1.5 text-[12px]"
          >
            <StatusIcon status={check.status} />
            <span className="min-w-0 flex-1">
              <span className="font-medium">{check.label}</span>
              <span className="vt-muted"> — {check.detail}</span>
              {check.remedy && (
                <span className="mt-0.5 block text-[11px] text-[var(--warn)]">{check.remedy}</span>
              )}
            </span>
          </li>
        ))}
      </ul>

      <Button
        variant="subtle"
        size="sm"
        className="self-start"
        data-testid="push-send-test"
        disabled={test.isPending}
        onClick={() => void sendTest()}
      >
        {test.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
        Send a test to this device
      </Button>
    </section>
  );
}
